/**
 * Periods are half-open [from, to) everywhere, matching the API and the database.
 * Dates are plain `YYYY-MM-DD` strings in UTC — the dashboard reasons in whole days.
 */
export interface Period {
  from: string;
  to: string;
}

export interface PeriodPreset {
  id: string;
  label: string;
  build: (today: Date) => Period;
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function monthStart(date: Date, offsetMonths = 0): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offsetMonths, 1));
}

/** Exclusive upper bound covering today in full. */
function tomorrow(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1));
}

export const PERIOD_PRESETS: PeriodPreset[] = [
  {
    id: 'this-month',
    label: 'Этот месяц',
    build: (today) => ({ from: iso(monthStart(today)), to: iso(tomorrow(today)) }),
  },
  {
    id: 'prev-month',
    label: 'Прошлый месяц',
    build: (today) => ({ from: iso(monthStart(today, -1)), to: iso(monthStart(today)) }),
  },
  {
    id: 'prev-prev-month',
    /** Месяц, по которому уже сошлись данные: табель закрыт, правки внесены. */
    label: 'Позапрошлый месяц',
    build: (today) => ({ from: iso(monthStart(today, -2)), to: iso(monthStart(today, -1)) }),
  },
  {
    id: 'last-3-months',
    label: '3 месяца',
    build: (today) => ({ from: iso(monthStart(today, -2)), to: iso(tomorrow(today)) }),
  },
  {
    id: 'this-year',
    label: 'Этот год',
    build: (today) => ({
      from: `${today.getUTCFullYear()}-01-01`,
      to: iso(tomorrow(today)),
    }),
  },
];

export function defaultPeriod(today: Date = new Date()): Period {
  // Previous month: the current one is always partial, which makes a first
  // impression of "everyone works less than expected".
  return PERIOD_PRESETS[1]!.build(today);
}

/** Which preset, if any, produced the given period — for highlighting the button. */
export function matchPreset(period: Period, today: Date = new Date()): string | undefined {
  return PERIOD_PRESETS.find((preset) => {
    const built = preset.build(today);
    return built.from === period.from && built.to === period.to;
  })?.id;
}
